/**
 * Chat transcript reducer + sessionStorage persistence. sessionStorage keeps the transcript
 * through a page refresh, and the tab's conversation id with it, so the agent picks its own
 * stored copy of the conversation back up on the next socket.
 */

import type {
    AssistantMessage, ChangeFrame, ChangeMessage, ChangePhase, ChangeProgress, ChatAction,
    ChatMessage, ChatState, ChatSubject, StepMessage, SystemMessage,
} from '@/types/chat';

import { groupTranscript, stepRequest, stepTitle } from './steps';

export const STORAGE_KEY = 'vms.chat.transcript.v1';
const CONVERSATION_KEY = 'vms.chat.conversation.v1';

/**
 * This tab's conversation id, created on first use. The agent keys its stored
 * conversation by it, so a reconnect or a reload resumes the conversation.
 */
export function getConversationId(): string {
    try {
        const storedId = sessionStorage.getItem(CONVERSATION_KEY);
        if (storedId !== null) {
            return storedId;
        }
        const createdId = crypto.randomUUID();
        sessionStorage.setItem(CONVERSATION_KEY, createdId);
        return createdId;
    } catch {
        return crypto.randomUUID(); // no storage: each socket starts a new conversation
    }
}

/** Forget this tab's conversation id, so the next socket starts a new conversation. */
export function startNewConversation(): void {
    try {
        sessionStorage.removeItem(CONVERSATION_KEY);
    } catch {
        /* private mode — nothing was stored */
    }
}

export function makeInitialState(): ChatState {
    return { messages: [], isStreaming: false };
}

function isStreaming(message: ChatMessage): message is AssistantMessage {
    return message.role === 'assistant' && message.status === 'streaming';
}

/** The streaming bubble (there is at most one) rewritten by `change`. */
function mapStreaming(
    messages: ChatMessage[], change: (bubble: AssistantMessage) => AssistantMessage,
): ChatMessage[] {
    return messages.map((message) => (isStreaming(message) ? change(message) : message));
}

function isChange(message: ChatMessage): boolean {
    return message.role === 'change';
}

/** The tool that starts a search. Each report of the search carries the id of its step. */
const FIND_CHANGE_TOOL = 'find_change_in_video';

/** The phases of a search that ended without a moment. */
const FAILED_ENDS: ChangePhase[] = ['notObserved', 'unclear'];

function isSearchStep(message: ChatMessage): message is StepMessage {
    return message.role === 'step' && message.name === FIND_CHANGE_TOOL;
}

/** The step of the newest search: only its card draws. */
function findNewestSearch(messages: ChatMessage[]): string | undefined {
    return [...messages].reverse().find(isSearchStep)?.stepId;
}

/**
 * The turn is over — it ended, was stopped, or its socket went away — so whatever it left in
 * flight hears nothing more: the bubble takes its final status, a running step is interrupted,
 * a pending approval expires, and an approved write whose outcome never came shows a plain
 * "Approved" (result: null).
 */
function endTurn(messages: ChatMessage[], status: 'done' | 'error'): ChatMessage[] {
    return mapStreaming(messages, (bubble) => ({ ...bubble, status })).map((message) => {
        if (message.role === 'step' && message.status === 'running') {
            return { ...message, status: 'interrupted' as const };
        }
        if (message.role === 'confirm' && message.status === 'pending') {
            return { ...message, status: 'expired' as const };
        }
        if (message.role === 'confirm' && message.status === 'approved' &&
            message.result === undefined) {
            return { ...message, result: null };
        }
        return message;
    });
}

function append(state: ChatState, message: ChatMessage): ChatState {
    return { ...state, messages: [...state.messages, message] };
}

function getSystemMessage(
    text: string, status: SystemMessage['status'], id = `sys:${Date.now()}`,
): SystemMessage {
    return { id, role: 'system', text, status };
}

/**
 * Place an item below the text streamed so far and, while the turn still streams, open a fresh
 * assistant bubble under it, so later deltas land below the item instead of flowing back into
 * the bubble above.
 */
function appendBelowText(state: ChatState, item: ChatMessage): ChatState {
    if (!state.isStreaming) { return append(state, item); }
    return {
        ...state,
        messages: [
            ...mapStreaming(state.messages, (bubble) => ({ ...bubble, status: 'done' })),
            item,
            { id: `${item.id}:a`, role: 'assistant', text: '', status: 'streaming' },
        ],
    };
}

/**
 * The agent sends the image of a slot only when its frame changes, so a later report of the same
 * frame has no image.
 */
function keepSlotImage(reportFrame?: ChangeFrame, previousFrame?: ChangeFrame):
ChangeFrame | undefined {
    if (reportFrame === undefined || reportFrame.image !== undefined ||
        previousFrame?.ms !== reportFrame.ms || previousFrame.image === undefined) {
        return reportFrame;
    }
    return { ...reportFrame, image: previousFrame.image };
}

/** A new search clears the previous report, so that report is of the search of `report`. */
function mergeProgress(previousReport: ChangeProgress | undefined, report: ChangeProgress):
ChangeProgress {
    if (previousReport === undefined) { return report; }
    return {
        ...report,
        before: keepSlotImage(report.before, previousReport.before),
        after: keepSlotImage(report.after, previousReport.after),
    };
}

/** The search is over for the chat: its card goes, and its images with it. */
function endChangeSearch(state: ChatState): ChatState {
    if (state.changeReport === undefined && !state.messages.some(isChange)) { return state; }
    return {
        ...state,
        messages: state.messages.filter((message) => !isChange(message)),
        changeReport: undefined,
    };
}

/**
 * The card of a find_change_in_video call draws with its first report, which the search sends
 * as it starts: the operator sees the search while the model makes other calls. A search that a
 * newer one replaced, as when the chat starts over, draws nothing: one card shows at a time.
 */
function showChangeReport(state: ChatState, report: ChangeProgress): ChatState {
    const { stepId } = report;
    const newestStepId = findNewestSearch(state.messages);
    if (newestStepId !== undefined && newestStepId !== stepId) { return state; }
    const changeReport = mergeProgress(state.changeReport, report);
    const shownCard = state.messages.find(isChange);
    const card: ChangeMessage = {
        // A card that draws again below the text needs a new id: the bubble below it takes its
        // id from the card.
        id: shownCard?.id ?? `change:${stepId}:${state.messages.length}`,
        role: 'change',
        stepId,
        progress: changeReport,
    };
    if (shownCard === undefined) {
        return appendBelowText({ ...state, changeReport }, card);
    }
    const messages = state.messages.map((message) => (isChange(message) ? card : message));
    return { ...state, messages, changeReport };
}

/**
 * Every rule of the change card. The card shows the report that the state keeps, so a card that
 * the answer text removed draws again with its images when the search reports after the text.
 */
function reduceChangeCard(state: ChatState, action: ChatAction): ChatState {
    switch (action.type) {
        case 'change_progress':
            return showChangeReport(state, action.progress);

        // The answer replaces the card: the card only fills the wait for it.
        case 'assistant_delta':
            if ((action.text ?? '').trim() === '') { return state; }
            return { ...state, messages: state.messages.filter((message) => !isChange(message)) };

        // A new search replaces the last one. Any other call shows that the chat went on past a
        // search that found no moment, whose frames then show no answer.
        case 'tool_step': {
            const { changeReport } = state;
            const isRunning = action.status === 'running';
            const isSearchReplaced = isRunning && action.name === FIND_CHANGE_TOOL;
            const isFailedSearchLeft = isRunning && changeReport !== undefined &&
                FAILED_ENDS.includes(changeReport.phase);
            const isSearchStepFailed = action.status === 'error' &&
                action.id === changeReport?.stepId;
            return isSearchReplaced || isFailedSearchLeft || isSearchStepFailed
                ? endChangeSearch(state)
                : state;
        }

        case 'assistant_done':
            return endChangeSearch(state);

        default:
            return state;
    }
}

export function reduceChat(state: ChatState, action: ChatAction): ChatState {
    return reduceMessages(reduceChangeCard(state, action), action);
}

/** The transcript without its change card, which `reduceChangeCard` owns. */
function reduceMessages(state: ChatState, action: ChatAction): ChatState {
    switch (action.type) {
        case 'load':
            return { messages: action.messages ?? [], isStreaming: false };

        case 'user_send':
            return {
                isStreaming: true,
                messages: [
                    ...state.messages,
                    {
                        id: action.id, role: 'user', text: action.text, status: 'sent',
                        subject: action.subject, findChange: action.findChange,
                    },
                    { id: `${action.id}:a`, role: 'assistant', text: '', status: 'streaming' },
                ],
            };

        case 'assistant_delta':
            return {
                ...state,
                messages: mapStreaming(state.messages,
                    (bubble) => ({ ...bubble, text: bubble.text + (action.text ?? '') })),
            };

        // The turn ended: by the agent's word, by /stop, or with its socket.
        case 'assistant_done':
            if (!state.isStreaming) { return state; }
            return {
                isStreaming: false,
                messages: endTurn(state.messages, action.stop === 'error' ? 'error' : 'done'),
            };

        // An error the agent reported. Not the end of a turn: a failed turn also sends
        // assistant_done, and an error a command drew mid-turn (/model) leaves it streaming.
        case 'system_error':
            return append(state, getSystemMessage(action.text, 'error'));

        // A local note (e.g. /help output); deltas keep flowing to the bubble above it.
        case 'system_note':
            return append(state, getSystemMessage(action.text, 'info', action.id));

        // /clear — drop the whole transcript. The caller also starts a new
        // conversation, so the agent's side resets with it.
        case 'clear':
            return makeInitialState();

        case 'widget':
            return appendBelowText(state, {
                id: action.id, role: 'widget', kind: action.kind, data: action.data,
                status: 'done',
            });

        // A write approval card. The model's post-approval sentence lands below the card:
        // flowing into the bubble above, it would read as claiming the change before the
        // operator approved it.
        case 'confirm_request':
            return appendBelowText(state, {
                id: `c:${action.confirmId}`, role: 'confirm', confirmId: action.confirmId,
                name: action.name, description: action.description, args: action.args,
                status: 'pending',
            });

        case 'confirm_resolved': {
            const messages = state.messages.map((message) =>
                message.role === 'confirm' && message.confirmId === action.confirmId
                    ? { ...message, status: action.status }
                    : message,
            );
            return { ...state, messages };
        }

        // The outcome of an approved write (confirm_result envelope): attach the
        // executed tool's result to its confirm card so it can show Applied/Failed
        // and deep-link a created entity (e.g. the new rule's id).
        case 'confirm_result': {
            const messages = state.messages.map((message) =>
                message.role === 'confirm' && message.confirmId === action.confirmId
                    ? { ...message, result: action.result, resultError: action.isError }
                    : message,
            );
            return { ...state, messages };
        }

        // A bookmark or case a tool created, drawn below the text streamed so far.
        case 'created':
            return appendBelowText(state, {
                id: action.id, role: 'created', record: action.record, status: 'done',
            });

        // A tool-step activity line (one per agent tool call). 'running' inserts a
        // new line; 'done'/'error' updates the matching line by stepId.
        case 'tool_step': {
            if (action.status === 'running') {
                return appendBelowText(state, {
                    id: `step:${action.id}`, role: 'step', stepId: action.id,
                    name: action.name, args: action.args, status: 'running',
                });
            }
            const messages = state.messages
                .map((message): ChatMessage => {
                    if (message.role === 'step' && message.stepId === action.id) {
                        return {
                            ...message,
                            status: action.status,
                            images: action.images,
                            items: action.items,
                        };
                    }
                    return message;
                });
            return { ...state, messages };
        }

        default:
            return state;
    }
}

export function loadTranscript(): ChatState {
    try {
        const rawText = sessionStorage.getItem(STORAGE_KEY);
        if (rawText === null) { return makeInitialState(); }
        const parsedValue: unknown = JSON.parse(rawText);
        if (!Array.isArray(parsedValue)) { return makeInitialState(); }
        // What this build wrote in an earlier tab, so it is read back as what it
        // was written as; a malformed store throws and lands on the catch. A turn
        // the refresh cut off hears nothing more from its socket.
        const messages = endTurn(parsedValue as ChatMessage[], 'done');
        return { messages, isStreaming: false };
    } catch {
        return makeInitialState();
    }
}

export function saveTranscript(messages: ChatMessage[]): void {
    try {
        // A change card lives only while its turn runs.
        const storedMessages = messages.filter((message) => !isChange(message));
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storedMessages));
    } catch {
    /* quota / private mode — transcript is best-effort */
    }
}

/**
 * Drop the persisted transcript and its conversation id. The conversation is
 * about one organization's cameras, events, and objects, so it must not follow
 * the user into the next one — see auth/orgScope.ts, which calls this on every
 * org switch.
 */
export function clearTranscript(): void {
    startNewConversation();
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
    /* private mode — nothing was persisted anyway */
    }
}

/** What a message was about, as its annotation names it. */
function getSubjectPhrase(subject: ChatSubject): string {
    const what = subject.kind === 'area' ? 'an area' : subject.label;
    return `${what} on ${subject.cameraName}`;
}

/** A tool call as its step line opens onto it: the title, then the request it sent. */
function stepToMarkdown(step: StepMessage): string {
    const request = ['```json', ...stepRequest(step).split('\n'), '```'];
    return [`- ${stepTitle(step)}`, ...request.map((line) => `  ${line}`)].join('\n');
}

/**
 * Serialize the transcript to Markdown (the /copy command) — for pasting into
 * a ticket or incident report. It follows what the panel draws: conversation
 * text verbatim, each tool call with its request, widgets and confirms as
 * one-line annotations.
 */
export function convertTranscriptToMarkdown(messages: ChatMessage[]): string {
    const lines: string[] = [];
    for (const item of groupTranscript(messages)) {
        if (item.kind === 'steps') {
            lines.push(...item.steps.map(stepToMarkdown));
            continue;
        }
        const { message } = item;
        switch (message.role) {
            case 'user':
                lines.push(message.subject
                    ? `**You** _(about ${getSubjectPhrase(message.subject)})_**:** ${message.text}`
                    : `**You:** ${message.text}`);
                break;
            case 'assistant':
                if (message.text) { lines.push(`**Assistant:**\n${message.text}`); }
                break;
            case 'system':
                lines.push(`_system: ${message.text}_`);
                break;
            case 'widget':
                lines.push(`- widget: ${message.kind}`);
                break;
            case 'created':
                lines.push(message.record.kind === 'bookmark'
                    ? `- bookmark created: ${message.record.bookmark.description}`
                    : `- case created: ${message.record.number} ${message.record.title}`);
                break;
            case 'confirm': {
                // result: null = approved but no outcome recorded (settled turn).
                const outcome = message.resultError
                    ? ', failed'
                    : message.result !== null && message.result !== undefined ? ', applied' : '';
                lines.push(`- confirm: ${message.name} (${message.status}${outcome})`);
                break;
            }
            default:
                break;
        }
    }
    return lines.join('\n\n');
}
